import { trace } from "@opentelemetry/api";
import { createMiddleware, json } from "@tanstack/react-start";
import { getCookie, getQuery, setCookie } from "@tanstack/react-start/server";
import type { CoachSelect } from "./schemas";

/**
 * Authenticates requests by loading coach from locationId cookie or query params
 * Adds authenticated coach to request context
 */
export const requireCoachAuth = createMiddleware({ type: "function" }).server<{
  coach: CoachSelect;
}>(async ({ next, context: _context }) => {
  let locationId: string | null = null;

  const queryLocationId = getQuery()["locationId"] || getQuery()["location_id"];

  if (queryLocationId) {
    locationId = `${queryLocationId}`;
  } else {
    const cookie = getCookie("locationId");
    if (cookie) {
      locationId = cookie;
    }
  }

  // If no locationId, continue without setting coach
  if (!locationId || typeof locationId !== "string") {
    throw json(
      {
        error: "No locationId found",
      },
      { status: 403 },
    );
  }

  try {
    const { getCoachByLocationId } = await import("~/lib/server/coach");
    const coach = (await getCoachByLocationId(locationId)) ?? null;

    if (coach) {
      setCookie("locationId", locationId, {
        path: "/",
        secure: true,
        sameSite: "none",
        expires: new Date(Date.now() + 1000 * 60 * 60 * 24 * 30), // 30 days
      });

      trace.getActiveSpan()?.setAttribute("coachId", coach.id);

      // Context is merged with the next context
      return next({
        context: { coach },
      });
    }
  } catch (e) {
    console.error(e);
    // Don't throw errors, just continue
  }

  throw json(
    {
      error: "No coach found",
    },
    { status: 403 },
  );
});
